import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { BrandingConfig, BRANDING_DEFAULTS } from './branding.model';

/**
 * Lädt zur Laufzeit /assets/branding/branding.json und setzt
 * CSS-Custom-Properties auf :root. Falls die Datei fehlt oder
 * nicht lesbar ist, werden die Defaults aus BRANDING_DEFAULTS verwendet –
 * die App startet immer fehlerfrei.
 *
 * Im Container wird das host-seitige shared-assets-Verzeichnis via Nginx
 * auf /assets/branding/ gemountet, sodass alle Frontends dieselbe Quelle nutzen.
 */
@Injectable({ providedIn: 'root' })
export class BrandingService {

  private _config: Required<BrandingConfig> = { ...BRANDING_DEFAULTS };

  get config(): Required<BrandingConfig> {
    return this._config;
  }

  /** Logo für helle Hintergründe. */
  get logoUrl(): string {
    return this._config.logo;
  }

  /** Logo für dunkle Hintergründe (z. B. blaue Toolbar). */
  get logoDarkUrl(): string {
    return this._config.logoDark;
  }

  constructor(private http: HttpClient) {}

  /**
   * Wird als APP_INITIALIZER aufgerufen. Lädt branding.json und setzt
   * CSS-Variablen, bevor die App rendert.
   */
  async load(): Promise<void> {
    try {
      const loaded = await firstValueFrom(
        this.http.get<BrandingConfig>('/assets/branding/branding.json')
      );
      this._config = {
        appName:              loaded.appName              ?? BRANDING_DEFAULTS.appName,
        logo:                 loaded.logo                 ?? BRANDING_DEFAULTS.logo,
        logoDark:             loaded.logoDark             ?? BRANDING_DEFAULTS.logoDark,
        accentColor:          loaded.accentColor          ?? BRANDING_DEFAULTS.accentColor,
        accentColorSecondary: loaded.accentColorSecondary ?? BRANDING_DEFAULTS.accentColorSecondary,
      };
    } catch {
      // branding.json fehlt oder ist nicht lesbar – Defaults beibehalten, kein Fehler
      this._config = { ...BRANDING_DEFAULTS };
    }

    this.applyCssVariables();
  }

  private applyCssVariables(): void {
    const root = document.documentElement;
    root.style.setProperty('--docuty-accent-color', this._config.accentColor);
    root.style.setProperty('--docuty-accent-color-secondary', this._config.accentColorSecondary);
  }
}
